/**
 * SCR-205: PII Scan Modal
 * BRD-014: Client-Side Document Redaction | US-205: Scan Before Upload
 *
 * Modal that scans a document for PII before upload.
 * If PII is found, user is prompted to review and redact.
 *
 * Flow:
 * 1. User drops file → This modal opens
 * 2. File is scanned client-side for PII
 * 3. If PII found → Show RedactionPreviewModal
 * 4. If no PII → Continue to normal upload
 */

import { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  ProgressBar,
  Icon,
} from '@myvault/shared';
import { usePIIScan, getPatternLabel, getSeverityColor, type ScanResult } from '../hooks/usePIIScan';

interface PIIScanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: File | null;
  onPIIFound: (result: ScanResult) => void;
  onNoPII: (file: File) => void;
  onCancel: () => void;
}

export function PIIScanModal({
  open,
  onOpenChange,
  file,
  onPIIFound,
  onNoPII,
  onCancel,
}: PIIScanModalProps) {
  const { scanFile, isScanning, progress, currentPage, totalPages, error, scanResult, reset } =
    usePIIScan();
  const [hasStarted, setHasStarted] = useState(false);

  // Start scanning when modal opens with a file
  useEffect(() => {
    if (open && file && !hasStarted) {
      setHasStarted(true);
      scanFile(file).catch(() => {
        // Error is handled in state
      });
    }
  }, [open, file, hasStarted, scanFile]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setHasStarted(false);
      reset();
    }
  }, [open, reset]);

  // Handle scan completion
  useEffect(() => {
    if (scanResult && file) {
      if (scanResult.piiFound) {
        // Give user a moment to see the result
        setTimeout(() => {
          onPIIFound(scanResult);
        }, 500);
      } else {
        // No PII - continue to upload
        setTimeout(() => {
          onNoPII(file);
        }, 1000);
      }
    }
  }, [scanResult, file, onPIIFound, onNoPII]);

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };

  const getScanStatusMessage = () => {
    if (error) return 'Scan mislukt';
    if (scanResult?.piiFound) return 'PII gedetecteerd!';
    if (scanResult && !scanResult.piiFound) return 'Geen PII gevonden';
    if (isScanning) return `Pagina ${currentPage} van ${totalPages} scannen...`;
    return 'Voorbereiden...';
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !isScanning) {
          handleCancel();
        }
      }}
      title="Document scannen op gevoelige gegevens"
      size="md"
    >
      <div className="space-y-6">
        {/* File info */}
        {file && (
          <div className="flex items-center gap-4 p-4 bg-mv-gray-50 rounded-lg">
            <Icon name="documentText" className="w-8 h-8 text-mv-blue-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-mv-gray-900 truncate">{file.name}</p>
              <p className="text-sm text-mv-gray-500">
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </p>
            </div>
          </div>
        )}

        {/* Scanning progress */}
        {isScanning && (
          <div className="space-y-4">
            <div className="flex items-center justify-center py-8">
              <div className="relative">
                <Icon
                  name="magnifyingGlass"
                  className="w-16 h-16 text-mv-blue-600 animate-pulse"
                />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-mv-blue-600 rounded-full flex items-center justify-center">
                  <Icon name="shieldCheck" variant="solid" size="sm" className="text-white" />
                </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-lg font-medium text-mv-gray-900">{getScanStatusMessage()}</p>
              <p className="text-sm text-mv-gray-500 mt-1">
                Zoeken naar BSN, IBAN, telefoonnummers, e-mailadressen...
              </p>
            </div>
            <ProgressBar value={progress} />
          </div>
        )}

        {/* Scan result: PII found */}
        {scanResult?.piiFound && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <Icon name="exclamationTriangle" className="w-8 h-8 text-amber-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-900">
                  {scanResult.totalMatches} gevoelige gegeven(s) gevonden
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  U wordt doorgestuurd naar het redactie-scherm
                </p>
              </div>
            </div>

            {/* Summary by type */}
            <div className="border border-mv-gray-200 rounded-lg divide-y divide-mv-gray-200">
              {Object.entries(scanResult.matchesByType).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: getSeverityColor(
                        type === 'BSN' || type === 'IBAN' ? 'high' :
                        type === 'EMAIL' || type.includes('PHONE') ? 'medium' : 'low'
                      )}}
                    />
                    <span className="text-sm text-mv-gray-700">{getPatternLabel(type)}</span>
                  </div>
                  <span className="text-sm font-medium text-mv-gray-900">{count}x</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scan result: No PII */}
        {scanResult && !scanResult.piiFound && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <Icon name="checkCircle" className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-900">Geen gevoelige gegevens gevonden</p>
                <p className="text-sm text-green-700 mt-1">
                  Document wordt geüpload...
                </p>
              </div>
            </div>
            <ProgressBar value={100} />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <Icon name="exclamationCircle" className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">Scan mislukt</p>
              <p className="text-sm text-red-700 mt-1">{error.message}</p>
            </div>
          </div>
        )}

        {/* Privacy info */}
        <div className="flex items-start gap-3 p-3 bg-mv-blue-50 border border-mv-blue-200 rounded-lg">
          <Icon name="shieldCheck" className="w-5 h-5 text-mv-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-mv-blue-800">
            <strong>Privacy:</strong> De scan vindt plaats in uw browser. Het document wordt pas
            naar de server gestuurd nadat u eventuele gevoelige gegevens heeft verwijderd.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-4 border-t border-mv-gray-200">
          <Button variant="secondary" onClick={handleCancel} disabled={isScanning}>
            Annuleren
          </Button>
          {error && (
            <Button
              variant="primary"
              onClick={() => file && scanFile(file)}
            >
              Opnieuw proberen
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default PIIScanModal;
